/**
 * RenderlessStore — tracks which source rows are "render-less": their
 * default column renderer has been MATERIALIZED into a real renderer child
 * (copy-on-write), so the hydrator must NOT re-synthesize a virtual one
 * beneath them.
 *
 * The same copy-on-write shape as `AliasInstanceStore` (detach/reset): a
 * source starts render-ful — `hydrateDefaultRenderer` synthesizes a virtual
 * `virtual(r_<hex>|renderer)` child each hydrate, and renderer customizations
 * live on the source's attribute bag. On first renderer customization the
 * virtual renderer is promoted to a real row and the source flips
 * render-less; from then on the persisted renderer row is the source of truth
 * and synthesis is gated off. `clear` is the reset-to-default analogue.
 *
 * Renderer lookup: `sheets/src/rows/variants/renderer-lookup.ts`; alias
 * hydration: `sheets/.docs/content/rows-aliases.md`.
 *
 * Emits `RowDataChanged(sourceId, "renderless")` so dependents re-evaluate.
 */

import { Actor, type InvalidationSink, RowDataChanged } from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

export class RenderlessStore {
  static readonly ACTOR = new Actor("RenderlessStore");

  readonly #renderlessRows = new Set<RowLocalId>();

  readonly #sink: InvalidationSink;
  constructor(sink: InvalidationSink) {
    this.#sink = sink;
  }

  /** True when `sourceId`'s default renderer has been materialized (COW). */
  isRenderless(sourceId: RowLocalId): boolean {
    return this.#renderlessRows.has(sourceId);
  }

  /** Seed render-less state during hydration (from the wire flag). Does NOT emit. */
  init(sourceId: RowLocalId): void {
    this.#renderlessRows.add(sourceId);
  }

  /** Flip a source render-less on first renderer customization. Emits. */
  mark(sourceId: RowLocalId): void {
    if (this.#renderlessRows.has(sourceId)) return;
    this.#renderlessRows.add(sourceId);
    this.#sink.emit(
      new RowDataChanged(RenderlessStore.ACTOR, sourceId, "renderless"),
    );
  }

  /** Reset to the synthesized default — drop the materialized renderer. Emits. */
  clear(sourceId: RowLocalId): void {
    if (!this.#renderlessRows.has(sourceId)) return;
    this.#renderlessRows.delete(sourceId);
    this.#sink.emit(
      new RowDataChanged(RenderlessStore.ACTOR, sourceId, "renderless"),
    );
  }
}
